import React, { useState, useEffect } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Form, Button, Checkbox, Collapse, message } from 'antd';
import { propertyService } from '@iso/services';
import HideShowModulesModalWrapper from './HideShowModulesModal.styles';

const { Panel } = Collapse;

// Hideable modules, tagged with the sidebar nav group they belong to (matching
// Layout/Sidebar/options.js). `value` is the sidebar item key stored in
// menu_config. Not every nav item is listed here — only the ones we allow
// hiding — but each is grouped as it appears in the nav bar.
const modulesList = [
  { label: 'Health and Safety', value: 'health-safety', group: 'safety' },
  { label: 'Incidents', value: 'incidents', group: 'safety' },
  { label: 'Safety Data Sheets', value: 'sds', group: 'safety' },
  { label: 'Visitor', value: 'manage-visitor', group: 'people' },
  { label: 'Training', value: 'training-module', group: 'people' },
  { label: 'Spray', value: 'spray', group: 'farm' },
  { label: 'Mobs', value: 'manage-mobs', group: 'farm' },
  { label: 'Livestock', value: 'manage-livestocks', group: 'farm' },
  { label: 'Harvest', value: 'harvest', group: 'farm' },
  { label: 'Dashboard', value: 'operations/dashboard', group: 'operations' },
  { label: 'Taskboard', value: 'operations/taskboard', group: 'operations' },
  { label: 'Planner', value: 'operations/planner', group: 'operations' },
  { label: 'Team Schedule', value: 'operations/team-schedule', group: 'operations' },
  { label: 'Time Tracker', value: 'operations/time-tracker', group: 'operations' },
  { label: 'Timesheets', value: 'operations/timesheets', group: 'operations' },
  { label: 'Activity', value: 'activity', group: 'monitor' },
];

// Nav group order + labels, matching NAV_GROUPS in the sidebar.
const GROUP_ORDER = [
  { key: 'management', label: 'Management' },
  { key: 'safety', label: 'Safety' },
  { key: 'people', label: 'People' },
  { key: 'farm', label: 'Farm' },
  { key: 'operations', label: 'Operations' },
  { key: 'monitor', label: 'Monitor' },
];

const allModuleValues = modulesList.map((m) => m.value);

// Groups that actually have hideable modules, in nav order.
const groups = GROUP_ORDER.map((g) => ({
  ...g,
  modules: modulesList.filter((m) => m.group === g.key),
})).filter((g) => g.modules.length > 0);

const HideShowModulesModal = (props) => {
  const { property, onSaved, cancelHideShowModules } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (property && property.id && props.visibleHideShowModules) {
      loadMenuConfig();
    }
  }, [property, props.visibleHideShowModules]);

  const loadMenuConfig = () => {
    if (!property || !property.id) return;

    propertyService.getMenuConfig(property.id).then((res) => {
      if (res.code === '0000' || res.code === 200) {
        const hiddenItems = res.menu_config || [];
        // Checked = visible = all modules except the hidden ones.
        form.setFieldsValue({ modules: allModuleValues.filter((v) => !hiddenItems.includes(v)) });
      } else {
        form.setFieldsValue({ modules: allModuleValues });
      }
    }).catch((err) => {
      console.error('Error loading menu config:', err);
      form.setFieldsValue({ modules: allModuleValues });
    });
  };

  const handleSubmit = () => {
    setLoading(true);

    form.validateFields().then((values) => {
      const visibleModules = values.modules || [];
      const hiddenItems = allModuleValues.filter((v) => !visibleModules.includes(v));

      propertyService.updateMenuConfig(property.id, hiddenItems).then((res) => {
        if (res.code === '0000' || res.code === 200) {
          message.success('Menu configuration updated successfully');
          setLoading(false);
          cancelHideShowModules();
          if (onSaved) onSaved();
          window.dispatchEvent(new CustomEvent('menuConfigUpdated'));
        } else {
          message.error(res.message || 'Failed to update menu configuration');
          setLoading(false);
        }
      }).catch((err) => {
        console.error('Error updating menu config:', err);
        message.error('Failed to update menu configuration');
        setLoading(false);
      });
    }).catch((info) => {
      console.log('Validate Failed:', info);
      setLoading(false);
    });
  };

  return (
    <HideShowModulesModalWrapper
      visible={props.visibleHideShowModules}
      onCancel={cancelHideShowModules}
      title="Hide/Show Modules"
      footer={[
        <Button key="back" onClick={cancelHideShowModules} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
      <Form form={form} scrollToFirstError layout="vertical">
        <div className="form-body">
          <Form.Item name="modules" className="isoInputWrapper">
            {/* One Checkbox.Group across all panels (checkboxes connect via
                context), grouped by nav header in collapsible sections that
                default to expanded. */}
            <Checkbox.Group style={{ width: '100%' }}>
              <Collapse defaultActiveKey={groups.map((g) => g.key)} ghost>
                {groups.map((g) => (
                  <Panel header={g.label} key={g.key} forceRender>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '8px' }}>
                      {g.modules.map((module) => (
                        <Checkbox key={module.value} value={module.value}>
                          {module.label}
                        </Checkbox>
                      ))}
                    </div>
                  </Panel>
                ))}
              </Collapse>
            </Checkbox.Group>
          </Form.Item>
        </div>
      </Form>
    </HideShowModulesModalWrapper>
  );
};

export default HideShowModulesModal;
